import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Switch,
  Textarea,
  Tooltip,
  useToast,
} from '@provenance/design';
import { isSealValidOn, toCalendarDate, todayCalendarDate } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Pencil, Plus, Sparkles, Stamp, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { toBase64 } from '../features/configuration/upload';
import {
  BackgroundChoice,
  FixBackgroundDialog,
  useCleanedImage,
} from '../features/configuration/ImageBackground';

interface Seal {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  active: boolean;
  validFrom: string | null;
  validTo: string | null;
  usedBySlots: number;
  /** Prints as a white box over the certificate. Null when the image could not be read. */
  solidBackground: boolean | null;
  createdAt: string;
}

interface SupportingStamp {
  id: string;
  imageUrl: string | null;
  defaultPosition: string;
  width: number;
  opacity: number;
  active: boolean;
}

/**
 * The seal library.
 *
 * A chamber is not one stamp. Some FTA certificates carry two, a seal re-registered with
 * an authority has a validity window, and an expired seal must be refused at approval
 * rather than silently printed — so validity is a first-class field here, not a note.
 */
export function Seals() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fixing, setFixing] = useState<Seal | null>(null);
  const [editing, setEditing] = useState<Seal | null>(null);

  const seals = useQuery({
    queryKey: ['seals'],
    queryFn: () => api.get<Seal[]>('/api/console/configuration/seals'),
  });
  const supporting = useQuery({
    queryKey: ['supporting-stamp'],
    queryFn: () => api.get<SupportingStamp | null>('/api/console/configuration/supporting-stamp'),
  });

  const setActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) =>
      api.post(`/api/console/configuration/seals/${input.id}/active`, { active: input.active }),
    onSuccess: (_result, input) => {
      toast.success(input.active ? 'Seal activated' : 'Seal deactivated');
      void queryClient.invalidateQueries({ queryKey: ['seals'] });
    },
    onError: (error) =>
      toast.error(
        'Could not change the seal',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  const rows = seals.data ?? [];
  // Today as a calendar day in the operator's timezone, compared the way approval compares
  // a certificate date (D-024). Comparing against "now" said "Outside validity" for a seal
  // valid from today, for the first ten hours of every Australian day.
  const today = toCalendarDate(todayCalendarDate())!;
  const usableToday = (seal: Seal) => isSealValidOn(seal, today);
  const expiring = rows.filter((s) => s.active && !usableToday(s));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Seal library"
        lede="The seals your operators can apply. A seal outside its validity window is refused at approval rather than printed, so a re-registration is a date change here — not a redeployment."
        actions={
          <Button variant="primary" leadingIcon={<Plus className="size-3.5" />} onClick={() => setUploadOpen(true)}>
            Add a seal
          </Button>
        }
      />

      {expiring.length > 0 && (
        <Callout
          tone="warn"
          icon={<AlertTriangle className="size-4" />}
          title={
            expiring.length === 1
              ? '1 active seal is outside its validity window'
              : `${expiring.length} active seals are outside their validity windows`
          }
        >
          {expiring.map((s) => s.name).join(', ')} cannot be applied to a certificate dated today.
          An approval that needs one will be refused.
        </Callout>
      )}

      {seals.isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Stamp className="size-5" />}
          title="No seals yet"
          description="Upload your chamber's seal as a PNG with a transparent background. A JPEG composites as a white box over the certificate."
          action={
            <Button variant="primary" onClick={() => setUploadOpen(true)}>
              Add your first seal
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rows.map((seal) => (
            <Card key={seal.id} className={seal.active ? undefined : 'opacity-70'}>
              <div className="flex items-start gap-3">
                <div className="grid size-20 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-1.5">
                  {seal.imageUrl ? (
                    <img
                      src={seal.imageUrl}
                      alt={`The ${seal.name} seal`}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <Stamp aria-hidden className="size-6 text-[var(--pv-faint)]" />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 truncate font-medium">{seal.name}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      leadingIcon={<Pencil className="size-3.5" />}
                      onClick={() => setEditing(seal)}
                      aria-label={`Edit the ${seal.name} seal`}
                    >
                      Edit
                    </Button>
                  </div>
                  {seal.description && (
                    <p className="mt-0.5 line-clamp-2 [font-size:12.5px] text-[var(--pv-muted)]">
                      {seal.description}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {!seal.active ? (
                      <Badge tone="neutral" size="sm">Inactive</Badge>
                    ) : usableToday(seal) ? (
                      <Badge tone="ok" size="sm" dot>Usable today</Badge>
                    ) : (
                      <Badge tone="stop" size="sm" dot>Outside validity</Badge>
                    )}
                    {seal.solidBackground && (
                      <Tooltip content="Its white background prints as a box over the certificate">
                        <span>
                          <Badge tone="warn" size="sm" dot>Solid background</Badge>
                        </span>
                      </Tooltip>
                    )}
                    {seal.usedBySlots > 0 && (
                      <Tooltip content="Stamp slots that default to this seal">
                        <span>
                          <Badge tone="neutral" size="sm">
                            {seal.usedBySlots} slot{seal.usedBySlots === 1 ? '' : 's'}
                          </Badge>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>

              {seal.solidBackground && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  leadingIcon={<Sparkles className="size-3.5" />}
                  onClick={() => setFixing(seal)}
                >
                  Make the background transparent
                </Button>
              )}

              <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-[var(--pv-line)] pt-3 [font-size:12px]">
                <div>
                  <dt className="text-[var(--pv-faint)]">Valid from</dt>
                  <dd className="pv-num">{formatDate(seal.validFrom) ?? 'Always'}</dd>
                </div>
                <div>
                  <dt className="text-[var(--pv-faint)]">Valid to</dt>
                  <dd className="pv-num">{formatDate(seal.validTo) ?? 'No end date'}</dd>
                </div>
              </dl>

              <div className="mt-3 flex items-center justify-between border-t border-[var(--pv-line)] pt-3">
                <span className="[font-size:12.5px] text-[var(--pv-muted)]">
                  {seal.active ? 'Available to operators' : 'Hidden from operators'}
                </span>
                <Switch
                  checked={seal.active}
                  disabled={setActive.isPending}
                  onCheckedChange={(active) => setActive.mutate({ id: seal.id, active })}
                  aria-label={`${seal.active ? 'Deactivate' : 'Activate'} the ${seal.name} seal`}
                />
              </div>
            </Card>
          ))}
        </div>
      )}

      <SupportingStampCard stamp={supporting.data ?? null} loading={supporting.isLoading} />

      <UploadSealDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      {editing && <EditSealDialog seal={editing} onClose={() => setEditing(null)} />}

      {fixing && (
        <FixBackgroundDialog
          open
          onOpenChange={(open) => !open && setFixing(null)}
          what="seal"
          name={fixing.name}
          load={() => api.blob(`/api/console/configuration/seals/${fixing.id}/image`)}
          save={async (file) => {
            await api.post(`/api/console/configuration/seals/${fixing.id}/image`, {
              filename: file.name,
              contentBase64: await toBase64(file),
            });
          }}
          onSaved={() => {
            toast.success('Seal updated', 'The next certificate uses the transparent version.');
            void queryClient.invalidateQueries({ queryKey: ['seals'] });
          }}
        />
      )}
    </div>
  );
}

function UploadSealDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [useCleaned, setUseCleaned] = useState(true);
  const cleaned = useCleanedImage(file, file?.name ?? 'seal.png');
  /** What is actually uploaded: the transparent version unless the administrator declined it. */
  const upload = cleaned?.state === 'solid' && useCleaned ? cleaned.cleaned : file;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  const reset = () => {
    setFile(null);
    setUseCleaned(true);
    setName('');
    setDescription('');
    setValidFrom('');
    setValidTo('');
  };

  const create = useMutation({
    mutationFn: async () =>
      api.post<{ id: string; warning: string | null }>('/api/console/configuration/seals', {
        name,
        description: description || null,
        filename: upload!.name,
        contentBase64: await toBase64(upload!),
        validFrom: validFrom || null,
        validTo: validTo || null,
      }),
    onSuccess: (result) => {
      // A JPEG is accepted rather than refused — a chamber may genuinely only hold one —
      // but the warning has to be loud, because the failure is visual and downstream.
      if (result.warning) toast.warn('Seal added, with a caveat', result.warning);
      else toast.success('Seal added', 'It is now available to your operators.');
      void queryClient.invalidateQueries({ queryKey: ['seals'] });
      onOpenChange(false);
      reset();
    },
    onError: (error) =>
      toast.error('Could not add the seal', error instanceof ApiError ? error.message : ''),
  });

  const previewUrl = upload ? URL.createObjectURL(upload) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          title="Add a seal"
          description="PNG with a transparent background. A JPEG will composite as a white box over the certificate's text."
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field label="Seal image" required>
              <div className="flex items-center gap-3">
                <div className="grid size-20 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-dashed border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-1.5">
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Upload aria-hidden className="size-5 text-[var(--pv-faint)]" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    const chosen = e.target.files?.[0] ?? null;
                    setFile(chosen);
                    if (chosen && !name) setName(chosen.name.replace(/\.[^.]+$/, ''));
                  }}
                  className="[font-size:13px] file:mr-3 file:rounded-[var(--pv-radius-sm)] file:border file:border-[var(--pv-line)] file:bg-[var(--pv-paper)] file:px-3 file:py-1.5 file:[font-size:13px]"
                  aria-label="Choose a seal image"
                />
              </div>
            </Field>

            {cleaned?.state === 'solid' && (
              <BackgroundChoice result={cleaned} useCleaned={useCleaned} onChange={setUseCleaned} what="seal" />
            )}

            <Field label="Name" required help="What operators will see when choosing a seal.">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Chamber seal — 2026" />
            </Field>

            <Field label="Description">
              <Textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Registered with DFAT in March 2026."
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Valid from" help="Leave blank for no start date.">
                <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </Field>
              <Field label="Valid to" help="After this date it is refused at approval.">
                <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
              </Field>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!upload || !name.trim() || cleaned?.state === 'checking'}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add seal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const POSITIONS = [
  { value: 'TOP_LEFT', label: 'Top left' },
  { value: 'TOP_RIGHT', label: 'Top right' },
  { value: 'BOTTOM_LEFT', label: 'Bottom left' },
  { value: 'BOTTOM_RIGHT', label: 'Bottom right' },
  { value: 'CENTER', label: 'Centre' },
];

function SupportingStampCard({ stamp, loading }: { stamp: SupportingStamp | null; loading: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<string | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  // Choosing a file must not be the same gesture as replacing the stamp. This image is
  // printed on every attachment a chamber stamps, the old one is not kept, and a file
  // picker is easy to mis-click — so the new image is shown first and replaces the old
  // one only on a second, deliberate press.
  const [pending, setPending] = useState<File | null>(null);

  const save = useMutation({
    mutationFn: async (input: { file?: File; defaultPosition?: string; width?: number; active?: boolean }) =>
      api.post('/api/console/configuration/supporting-stamp', {
        ...(input.file ? { filename: input.file.name, contentBase64: await toBase64(input.file) } : {}),
        ...(input.defaultPosition ? { defaultPosition: input.defaultPosition } : {}),
        ...(input.width ? { width: input.width } : {}),
        ...(input.active === undefined ? {} : { active: input.active }),
      }),
    onSuccess: () => {
      toast.success('Supporting-document stamp saved');
      setPending(null);
      void queryClient.invalidateQueries({ queryKey: ['supporting-stamp'] });
    },
    onError: (error) =>
      toast.error('Could not save the stamp', error instanceof ApiError ? error.message : ''),
  });

  if (loading) return <Skeleton className="h-40" />;

  const effectivePosition = position ?? stamp?.defaultPosition ?? 'BOTTOM_RIGHT';
  const effectiveWidth = width ?? stamp?.width ?? 120;

  return (
    <Card>
      <CardHeader
        title="Supporting-document stamp"
        description="Applied to exporter-supplied attachments — invoices, packing lists — when an operator stamps them. Its position here is only the starting point; the operator clicks the exact spot."
      />
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex shrink-0 items-center gap-3">
          <figure className="m-0">
            <div className="grid size-24 place-items-center rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-2">
              {stamp?.imageUrl ? (
                <img src={stamp.imageUrl} alt="The supporting-document stamp" className="max-h-full max-w-full object-contain" />
              ) : (
                <Stamp aria-hidden className="size-6 text-[var(--pv-faint)]" />
              )}
            </div>
            {pending && (
              <figcaption className="mt-1 text-center [font-size:11px] text-[var(--pv-faint)]">Now</figcaption>
            )}
          </figure>

          {pending && (
            <>
              <ArrowRight aria-hidden className="size-4 text-[var(--pv-faint)]" />
              <figure className="m-0">
                <div className="grid size-24 place-items-center rounded-[var(--pv-radius-sm)] border-2 border-[var(--pv-brand)] bg-[var(--pv-paper-sunk)] p-2">
                  <img
                    src={URL.createObjectURL(pending)}
                    alt="The replacement stamp"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                <figcaption className="mt-1 text-center [font-size:11px] font-medium">Replacement</figcaption>
              </figure>
            </>
          )}
        </div>

        <div className="grid min-w-[16rem] flex-1 gap-3 sm:grid-cols-2">
          <Field label="Default position">
            <NativeSelect
              value={effectivePosition}
              onChange={(e) => {
                setPosition(e.target.value);
                save.mutate({ defaultPosition: e.target.value });
              }}
            >
              {POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Width (points)">
            <Input
              type="number"
              value={effectiveWidth}
              onChange={(e) => setWidth(Number(e.target.value))}
              onBlur={() => width && save.mutate({ width })}
            />
          </Field>
        </div>

        <div className="flex flex-col items-end gap-2">
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg"
            className="sr-only"
            aria-label="Choose a supporting-document stamp image"
            onChange={(e) => setPending(e.target.files?.[0] ?? null)}
          />
          {pending ? (
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => setPending(null)}>
                Keep the current one
              </Button>
              <Button
                variant="primary"
                size="sm"
                loading={save.isPending}
                onClick={() => save.mutate({ file: pending })}
              >
                Replace it
              </Button>
            </div>
          ) : (
            <Button
              variant="secondary"
              leadingIcon={<Upload className="size-3.5" />}
              loading={save.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {stamp?.imageUrl ? 'Replace image' : 'Upload image'}
            </Button>
          )}
          {stamp && (
            <label className="flex items-center gap-2 [font-size:12.5px] text-[var(--pv-muted)]">
              Enabled
              <Switch
                checked={stamp.active}
                onCheckedChange={(active) => save.mutate({ active })}
                aria-label="Enable the supporting-document stamp"
              />
            </label>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Validity dates are calendar dates pinned to UTC midnight (D-024): read them in UTC. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** `YYYY-MM-DD` for a date input, from a stored calendar date. */
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Rename a seal, correct its validity window, or replace its image.
 *
 * Certificates already issued keep the image they were stamped with — the replaced file is
 * kept in storage and named in the audit trail — so this changes what prints next, never
 * what has been printed.
 */
function EditSealDialog({ seal, onClose }: { seal: Seal; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(seal.name);
  const [description, setDescription] = useState(seal.description ?? '');
  const [validFrom, setValidFrom] = useState(toDateInput(seal.validFrom));
  const [validTo, setValidTo] = useState(toDateInput(seal.validTo));
  const [file, setFile] = useState<File | null>(null);
  const [useCleaned, setUseCleaned] = useState(true);
  const cleaned = useCleanedImage(file, file?.name ?? 'seal.png');
  const replacement = cleaned?.state === 'solid' && useCleaned ? cleaned.cleaned : file;

  const datesInverted = Boolean(validFrom && validTo && validFrom > validTo);

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/console/configuration/seals/${seal.id}`, {
        name: name.trim(),
        description: description.trim() || null,
        validFrom: validFrom || null,
        validTo: validTo || null,
      });
      if (replacement) {
        await api.post(`/api/console/configuration/seals/${seal.id}/image`, {
          filename: replacement.name,
          contentBase64: await toBase64(replacement),
        });
      }
    },
    onSuccess: () => {
      toast.success('Seal updated', replacement ? 'The next certificate uses the new image.' : undefined);
      void queryClient.invalidateQueries({ queryKey: ['seals'] });
      onClose();
    },
    onError: (error) =>
      toast.error('Could not update the seal', error instanceof ApiError ? error.message : 'Something went wrong.'),
  });

  const previewUrl = replacement ? URL.createObjectURL(replacement) : seal.imageUrl;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={`Edit ${seal.name}`}
          description="Changes apply to the next certificate. Certificates already issued keep the seal they were stamped with."
        />
        <DialogBody>
          <div className="flex flex-col gap-3.5">
            <Field label="Image" help="Leave as it is, or choose a new PNG or JPEG to replace it.">
              <div className="flex items-center gap-3">
                <div className="grid size-20 shrink-0 place-items-center rounded-[var(--pv-radius-sm)] border border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] p-1.5">
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <Stamp aria-hidden className="size-5 text-[var(--pv-faint)]" />
                  )}
                </div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="[font-size:13px] file:mr-3 file:rounded-[var(--pv-radius-sm)] file:border file:border-[var(--pv-line)] file:bg-[var(--pv-paper)] file:px-3 file:py-1.5 file:[font-size:13px]"
                  aria-label="Choose a replacement image"
                />
              </div>
            </Field>

            {cleaned?.state === 'solid' && (
              <BackgroundChoice result={cleaned} useCleaned={useCleaned} onChange={setUseCleaned} what="seal" />
            )}

            <Field label="Name" required help="What operators see when choosing a seal.">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>

            <Field label="Description">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Valid from" help="Blank: no start date.">
                <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </Field>
              <Field
                label="Valid to"
                help="Blank: no end date."
                error={datesInverted ? 'This is before the valid-from date.' : undefined}
              >
                <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
              </Field>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || datesInverted || cleaned?.state === 'checking'}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
